import * as queryString from './QueryString';
import { getRouteParams, getRouteQuery } from './Route';
import requestLib from './Request';
import { setCommonAmalgamForGroupCatalogue } from './GroupCatalogueAmalgam';
import { getCompanyId, isCustomPage, setCommonAmalgam } from './Amalgam';
import { setCustomPageAmalgamObject } from './CustomPageAmalgam';

const catalogueAmalgamCache = {};
const catalogueMetaDataCache = {};
const getAmalgamPromises = {};

const setCommonAmalgamFromGroup = (amalgam) => {
  setCommonAmalgam({
    companyId: amalgam.companyId,
    companyName: amalgam.companyName,
    companyAddress: amalgam.companyAddress,
    companyDetails: amalgam.companyDetails,
    currencyCode: amalgam.currencyCode,
    currencySymbol: amalgam.currencySymbol,
    logoExists: amalgam.logoExists,
    logoVersion: amalgam.logoVersion,
    currencyOptions: amalgam.currencyOptions,
    isBlockedCompany: amalgam.isBlockedCompany,
    localeNumberFormats: amalgam.localeNumberFormats,
    paymentPlan: amalgam.paymentPlan,
    extendedTrialStart: amalgam.extendedTrialStart,
    i18n: amalgam.i18n,
    companyExperiments: amalgam.companyExperiments,
    visitorId: amalgam.visitorId,
    pureWhitelabel: amalgam.pureWhitelabel,
    userId: amalgam.userId,
    userPhone: isCustomPage() ? amalgam.userPhone : amalgam.phone,
    userName: isCustomPage() ? amalgam.userName : amalgam.name,
    userEmail: isCustomPage() ? amalgam.userEmail : amalgam.email,
    isPaid: amalgam.isPaid,
    planType: amalgam.planType,
    theme: amalgam.theme || amalgam.catalogueTheme,
    facebookPixelIds: amalgam.facebookPixelIds,
    googleAnalyticsIds: amalgam.googleAnalyticsIds,
    showReportAbuseLink: amalgam.showReportAbuseLink,
    isHomePage: amalgam.isHomePage,
    isCustomPage: amalgam.isCustomPage,
    hasCustomFrontPage: amalgam.hasCustomFrontPage,
    menuItems: amalgam.menuItems
  });
  setCommonAmalgamForGroupCatalogue(amalgam);
};

const getCatalogueCacheKey = () => {
  const { companySlug, randomSlug, customPageSlug } = getRouteParams();
  return !!customPageSlug || customPageSlug === ''
    ? `custom_page_${customPageSlug}`
    : `${companySlug}_${randomSlug}`;
};

export const getAmalgamObjectFromCache = () => {
  const key = getCatalogueCacheKey();
  return catalogueAmalgamCache[key] || {};
};

export const getGroupAmalgamObjectFromCache = () => {
  const key = getCatalogueCacheKey();
  return catalogueAmalgamCache[key];
};

if (window.amalgamObject && window.amalgamObject.isGroup) {
  const { companySlug, randomSlug, customPageSlug } = window.amalgamObject.routeSlugs;
  const key = window.amalgamObject.isCustomPage
    ? `custom_page_${customPageSlug}`
    : `${companySlug}_${randomSlug}`;
  catalogueAmalgamCache[key] = window.amalgamObject;
  catalogueMetaDataCache[key] = window.metaDataObject;
  setCommonAmalgamFromGroup(catalogueAmalgamCache[key]);
  if (key === `custom_page_${customPageSlug}`) {
    setCustomPageAmalgamObject(catalogueAmalgamCache[key]);
  }
}

export const getGroupAmalgam = () => {
  const { companySlug, randomSlug } = getRouteParams();
  const { referrer, referrerVal } = getRouteQuery();
  const key = `${companySlug}_${randomSlug}`;
  return makeRequestForGroupAmalgam({ randomSlug, companySlug, referrer, referrerVal, key });
};

export const getCustomPageGroupAmalgam = () => makeRequestForCustomPageAmalgam();

export const getViewAllCustomPageCatalogueAmalgam = ({ randomSlug, companySlug }) => {
  const { referrer, referrerVal } = getRouteQuery();
  const key = `${companySlug}_${randomSlug}`;
  return makeRequestForGroupAmalgam({ randomSlug, companySlug, referrer, referrerVal, key });
};

const makeRequestForGroupAmalgam = ({
  randomSlug,
  companySlug,
  customPageSlug,
  referrer,
  referrerVal,
  key
}) => {
  if (catalogueAmalgamCache[key]) {
    return Promise.resolve(catalogueAmalgamCache[key]);
  }

  if (getAmalgamPromises[key]) {
    return getAmalgamPromises[key];
  }
  getAmalgamPromises[key] = new Promise((resolve, reject) => {
    const query = queryString.stringify({
      companySlug,
      randomSlug,
      customPageSlug,
      referrer,
      referrerVal
    });

    requestLib({
      method: 'GET',
      url: `/group-amalgam?${query}`
    })
      .then((response) => {
        catalogueAmalgamCache[key] = response.amalgam;
        catalogueMetaDataCache[key] = response.metaData;
        resolve();
        delete getAmalgamPromises[key];
        if (key === `custom_page_${customPageSlug}`) {
          setCustomPageAmalgamObject(catalogueAmalgamCache[key]);
        }
        setCommonAmalgamFromGroup(catalogueAmalgamCache[key]);
      })
      .catch(() => {
        reject();
        delete getAmalgamPromises[key];
      });
  });
  return getAmalgamPromises[key];
};

const makeRequestForCustomPageAmalgam = () => {
  const { customPageSlug } = getRouteParams();
  const key = `custom_page_${customPageSlug}`;
  if (catalogueAmalgamCache[key]) {
    return Promise.resolve(catalogueAmalgamCache[key]);
  }

  if (getAmalgamPromises[key]) {
    return getAmalgamPromises[key];
  }
  getAmalgamPromises[key] = new Promise((resolve, reject) => {
    requestLib({
      method: 'GET',
      url: `/api/amalgam/custom-page?companyId=${getCompanyId()}&customPageSlug=${customPageSlug}`
    })
      .then((response) => {
        catalogueAmalgamCache[key] = response.amalgam;
        catalogueMetaDataCache[key] = response.metaData;
        resolve();
        delete getAmalgamPromises[key];

        setCommonAmalgamFromGroup(catalogueAmalgamCache[key]);
        setCustomPageAmalgamObject(catalogueAmalgamCache[key]);
        setCommonAmalgamForGroupCatalogue(catalogueAmalgamCache[key]);
      })
      .catch(() => {
        reject();
        delete getAmalgamPromises[key];
      });
  });
  return getAmalgamPromises[key];
};

export const isViewAllCatalogueAmalgamFetched = ({ randomSlug, companySlug }) => {
  const key = `${companySlug}_${randomSlug}`;
  if (catalogueAmalgamCache[key]) {
    setCommonAmalgamFromGroup(catalogueAmalgamCache[key]);
  }
  return !!catalogueAmalgamCache[key];
};
export const isGroupAmalgamFetched = () => !!getGroupAmalgamObjectFromCache();

export const getCachedCustomPageAmalgam = (customPageSlug) =>
  catalogueAmalgamCache[`custom_page_${customPageSlug}`];

export const getGroupHtmlThemeColor = () =>
  catalogueMetaDataCache[getCatalogueCacheKey()].htmlThemeColor;
export const getAllCatalogueIds = () => getAmalgamObjectFromCache().allCatalogueIds || [];
export const getShowcaseId = () => getAmalgamObjectFromCache().showcaseId;
export const getCatalogues = () => getAmalgamObjectFromCache().catalogues || [];
export const getCatalogueHost = () =>
  getAmalgamObjectFromCache().catalogueHost || 'https://quicksell.co';
export const shouldLoadCloudfront = () => getAmalgamObjectFromCache().loadCloudfront;
export const isPaid = () => getAmalgamObjectFromCache().isPaid;
export const areMoreCataloguesAvailable = () => getAmalgamObjectFromCache().moreCataloguesAvailable;
export const setMoreCataloguesAvailable = (moreCataloguesAvailable) => {
  if (catalogueAmalgamCache[getCatalogueCacheKey()]) {
    catalogueAmalgamCache[getCatalogueCacheKey()].moreCataloguesAvailable = moreCataloguesAvailable;
  }
};

export const shouldRemoveGradient = () => getAmalgamObjectFromCache().removeGroupCatalogueGradient;
export const shouldRemoveCatalogueTitle = () =>
  getAmalgamObjectFromCache().removeGroupCatalogueTitle;
export const shouldRemoveCatalogueCount = () =>
  getAmalgamObjectFromCache().removeCatalogueCountFromGroupCatalogue;

export const getHomePageHeadline = () => getAmalgamObjectFromCache().homePageHeadline;
export const getRequestGeo = () => getAmalgamObjectFromCache().requestGeo;
export const getHashedDomainTag = () => getAmalgamObjectFromCache().hashedDomainTag;
export const getReferrer = () => getAmalgamObjectFromCache().referrer;
export const getReferrerVal = () => getAmalgamObjectFromCache().referrerVal;
export const getVisitorId = () => getAmalgamObjectFromCache().visitorId;
export const getUA = () => getAmalgamObjectFromCache().ua;
export const getVisitorIp = () => getAmalgamObjectFromCache().visitorIp;
export const getCataloguesPosition = () => getAmalgamObjectFromCache().cataloguesPosition;
export const isHomePage = () => !!getAmalgamObjectFromCache().isHomePage;
export const shouldShowLargeLogo = () => !!getAmalgamObjectFromCache().largeGroupCatalogueLogo;
export const shouldShowMediumLogo = () => !!getAmalgamObjectFromCache().mediumGroupCatalogueLogo;
export const getSortOrder = () => getAmalgamObjectFromCache().sortOrder;
export const getVideoBanner = () => getAmalgamObjectFromCache().groupVideoBanner;
export const getVideoYoutubeId = () => getAmalgamObjectFromCache().groupVideoYoutubeId;
export const groupBackgroundContain = () => getAmalgamObjectFromCache().groupBackgroundContain;
export const shouldRemoveGroupCompanyHeader = () =>
  getAmalgamObjectFromCache().removeGroupCompanyHeader;
export const getPaymentStatus = () => getAmalgamObjectFromCache().paymentStatus || '';
export const getCompanyHomeLayout = () =>
  getAmalgamObjectFromCache().companyExperiments.companyHomeLayout;
export const getGroupCatalogueCustomAspectRatio = () =>
  getAmalgamObjectFromCache().companyExperiments.groupCatalogueCustomAspectRatio;

/* Show grid layout only when companyExperiment has grid view enabled and number of catalogue's are more than 3 */
export const showCataloguesInGridView = () =>
  getCompanyHomeLayout() === 'grid' &&
  getAmalgamObjectFromCache().allCatalogueIds &&
  getAmalgamObjectFromCache().allCatalogueIds.length > 3;

export const getCompanyDomains = () => getAmalgamObjectFromCache().companyDomains || {};
